import { FC } from 'react'

import { useParams } from 'react-router-dom'

import {
  ResourceCreateView,
  ResourceDetailView,
  ResourceListView,
  useResourceListPaging,
} from 'core/resources'
import {
  useCreditAgenciesList,
  useCreditAgencyCreate,
  useCreditAgencyGet,
  useCreditAgencyUpdate,
} from 'core/types/generated/credit-agencies/hooks'
import { resourceCatalog } from 'core/types/generated/resources'
import {
  creditAgenciesRequestSchema,
  creditAgenciesResponseSchema,
  creditAgenciesUpdateRequestSchema,
} from 'core/utils/generated/formSchemas'

import { asRequestBody, createdId, listRows, record } from './helpers'

const resource = resourceCatalog.creditAgencies

type ListQuery = Parameters<typeof useCreditAgenciesList>[0]['query']

export const CreditAgenciesList: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const paging = useResourceListPaging<ListQuery>('cursor')
  const result = useCreditAgenciesList({ companyId, query: paging.query })

  return (
    <ResourceListView
      {...listRows(result)}
      paging={paging}
      resource={resource}
      schema={creditAgenciesResponseSchema}
    />
  )
}

export const CreditAgencyCreate: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { mutateAsync } = useCreditAgencyCreate({ companyId })

  return (
    <ResourceCreateView
      onCreate={async (body) =>
        createdId(await mutateAsync(asRequestBody(body)))
      }
      resource={resource}
      schema={creditAgenciesRequestSchema}
    />
  )
}

export const CreditAgencyDetail: FC = () => {
  const { companyId, creditAgencyId } = useParams<{
    companyId: string
    creditAgencyId: string
  }>()
  const result = useCreditAgencyGet({ companyId, creditAgencyId })
  const update = useCreditAgencyUpdate({ companyId, creditAgencyId })

  return (
    <ResourceDetailView
      {...record(result)}
      onSave={async (body) => {
        await update.mutateAsync(asRequestBody(body))
      }}
      resource={resource}
      schema={creditAgenciesResponseSchema}
      updateSchema={creditAgenciesUpdateRequestSchema}
    />
  )
}
